import React, {useState} from 'react';
import {Row, Col, Form} from 'react-bootstrap';
import Button from '@/components/ui/Button';
import {BaseSettingsForm} from '@/components/forms';
import {useStoreActions, useStoreState} from 'easy-peasy';
import AsyncSelect from 'react-select/async';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import axios from 'axios';
import * as yup from 'yup';
import i18n from 'i18n';

interface Group {
    value: number;
    label: string;
}

interface User {
    id: number;
    username: string;
    email: string;
    active: boolean;
    groups: Group[];
}

interface UserSettingsFormProps {
    userId?: number;
    onSave?: () => void;
}

interface FormValues {
    username: string;
    email: string;
    active: boolean;
    groups: Group[];
}

interface GroupsResponse {
    data?: Group[];
    [key: string]: any;
}

const loadGroupsRequest = async (inputValue: string): Promise<GroupsResponse> => {
    try {
        const response = await axios.get<GroupsResponse>(window.URLS.groupsFieldChoices, {
            params: {
                query: inputValue,
            },
        });
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 400) {
            return error.response.data as unknown as GroupsResponse;
        }
        throw error;
    }
};

const loadGroupsRequestDebounced = AwesomeDebouncePromise(loadGroupsRequest, 280);

const UserSettingsFormSchema = yup.object({
    username: yup.string()
        .max(100, i18n.t('jt_us_max_username_length.text'))
        .required(i18n.t('jt_us_required_username.text')),
    email: yup.string()
        .trim(i18n.t('jt_us_trim_email.text'))
        .max(255, i18n.t('jt_us_max_email_length.text'))
        .required(i18n.t('jt_us_required_email.text')),
    active: yup.boolean(),
    groups: yup.array().of(
        yup.object({
            value: yup.number().required(),
            label: yup.string().required(),
        }).required(),
    ),
});

const UserSettingsForm: React.FC<UserSettingsFormProps> = React.memo(({
    userId = 0,
    onSave = () => { },
}) => {
    const users = useStoreState((state: any) => state.users.items);
    const selectedUser = users.find((user: User) => user.id === userId);
    const setUser = useStoreActions((actions: any) => actions.users.addOrEdit);

    const [groupOptions, setGroupOptions] = useState<Group[]>([]);

    const initialValues: FormValues = selectedUser || {
        username: '',
        email: '',
        active: true,
        groups: [],
    };

    const customErrorHandling = (error: any, formik: any) => {
        if (error.response?.status === 400) {
            const errorData = error.response?.data;

            if (errorData?.messageUsername) {
                formik.setFieldError('username', errorData.messageUsername);
                return true;
            }

            if (errorData?.messageEmail) {
                formik.setFieldError('email', errorData.messageEmail);
                return true;
            }

            if (errorData?.message) {
                formik.setFieldError('email', errorData.message);
                return true;
            }

            if (errorData?.error) {
                // Backend returns a generic error message, e.g. "This email is already in use."
                // Route it to the most relevant field (email in this case).
                const message = errorData.error;
                const field = /email/i.test(message) ? 'email' : 'username';
                formik.setFieldError(field, message);
                return true;
            }

            // Let BaseSettingsForm handle any other 400-shapes we don't explicitly know about
            return false;
        }
        return false;
    };

    const setReloadUsers = useStoreActions((actions: any) => actions.users.setReload);

    const customSuccessHandling = (response: any, actionName: string) => {
        setUser(response.data);
        setReloadUsers(true);
        onSave?.();
    };

    const getGroupOptions = async (inputValue: string): Promise<Group[]> => {
        const results = await loadGroupsRequestDebounced(inputValue);

        const selectAllOption: Group = {
            label: i18n.t('jt_us_select_all.text'),
            value: -1,
        };

        // Handle different response structures
        const groupsData = results.data || results || [];
        const groupChoices: Group[] = Array.isArray(groupsData)
            ? groupsData.map((group) => ({
                label: group.label,
                value: group.value,
            }))
            : [];

        setGroupOptions(groupChoices);

        return [
            ...(inputValue || !groupChoices.length ? [] : [selectAllOption]),
            ...groupChoices,
        ];
    };

    const onGroupSelection = (formik: any) => (value: readonly Group[] | null) => {
        if (value && value.length && value[value.length - 1].value === -1) {
            formik.setFieldValue('groups', groupOptions);
        } else {
            formik.setFieldValue('groups', value ? [...value] : []);
        }
    };

    return (
        <BaseSettingsForm
            entityId={userId || undefined}
            resourceUrl={window.URLS.usersResource}
            storeActionPath="users.addOrEdit"
            validationSchema={UserSettingsFormSchema}
            initialValues={initialValues}
            successMessageKey="jt_us_user_created_successfully.text"
            onSave={onSave}
            customErrorHandling={customErrorHandling}
            customSuccessHandling={customSuccessHandling}
            additionalStoreActions={[
                {
                    path: 'users.setReload',
                    value: true,
                },
            ]}
        >
            {(formik, loading) => (
                <>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="username" >
                                <Form.Label>{i18n.t('jt_username.text')}</Form.Label>
                                <Form.Control
                                    name="username"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.username}
                                    isInvalid={formik.touched.username && !!formik.errors.username}
                                    disabled={!!userId}
                                    autoFocus
                                    data-cy='user-settings-username-input'
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.username}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="email" >
                                <Form.Label>{i18n.t('jt_email_capitalized.text')}</Form.Label>
                                <Form.Control
                                    name="email"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.email}
                                    isInvalid={formik.touched.email && !!formik.errors.email}
                                    data-cy='user-settings-email-input'
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.email}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="groups" >
                                <Form.Label>{i18n.t('jt_groups.text')}</Form.Label>
                                <AsyncSelect<Group, true>
                                    isMulti
                                    cacheOptions
                                    defaultOptions={true}
                                    loadOptions={getGroupOptions}
                                    hideSelectedOptions={false}
                                    onChange={onGroupSelection(formik)}
                                    value={formik.values.groups}
                                    data-cy='user-settings-groups'
                                    noOptionsMessage={() => i18n.t('jt_us_no_groups_found.text')}
                                    loadingMessage={() => i18n.t('jt_us_loading_groups.text')}
                                    styles={{
                                        valueContainer: (provided) => ({
                                            ...provided,
                                            maxHeight: '100px',
                                            overflowY: 'scroll',
                                            scrollbarWidth: 'none',
                                            overflowStyle: 'none',
                                            '&::-webkit-scrollbar': {
                                                width: 0,
                                                height: 0,
                                            },
                                        }),
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" >
                                <Form.Check
                                    name="active"
                                    label={i18n.t('es_active.text')}
                                    type="switch"
                                    onChange={formik.handleChange}
                                    checked={formik.values.active}
                                    isInvalid={!!formik.errors.active}
                                    feedback={formik.errors.active}
                                    id="userActive"
                                    data-cy='user-settings-active-toggle'
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12} className="d-flex justify-content-end">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                data-cy='user-settings-save-btn'
                            >
                                {userId ? i18n.t('jt_update.text') : i18n.t('jt_create.text')}
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </BaseSettingsForm >
    );
});


export default UserSettingsForm; 